/**
 * MONSTER TOWN — NAMED ANGLES AND DEMOS FOR THE QA TOOLS. NPC CREW.
 *
 * Extends the harness via `registerAngle/registerSpot/registerDemo` only; it
 * never changes or removes an existing name.
 *
 * A DEMO THAT LIES IS WORSE THAN NO DEMO. `gift-open` returns how many presents
 * were actually OPENED, not how many were placed — the first version returned
 * "1 = placed" and hid a real bug where the monster walked over and then nothing
 * happened.
 */

import { bus } from '../core/events'
import { app } from '../app/context'
import { registerAngle, registerDemo, registerSpot, settle } from '../harness'
import { playerPosition, teleportPlayer } from '../monster/player'
import {
  dropGift,
  giftOpenCount,
  npcs,
  setPopulationCap,
  spawnNeighbour,
  townCentre,
  townNode,
} from './registry'

export function registerNpcHarness(): void {
  const node = townNode()
  if (node === null) return
  const centre = townCentre(node)
  const id = node.def.id

  registerAngle({ name: 'monster-creator', sceneId: id, target: centre, rot: 0, zoom: 0 })
  registerAngle({ name: 'monster-closeup', sceneId: id, target: centre, rot: 0, zoom: 0 })
  registerAngle({ name: 'town-life', sceneId: id, target: centre, rot: 1, zoom: 2 })
  registerSpot('plaza', id, centre)

  registerDemo('npcs', async () => {
    const n = townNode()
    if (n === null) return 0
    while (npcs.size < 4) {
      if (spawnNeighbour(n, undefined, false) === null) break
    }
    const c = townCentre(n)
    const ring = [...npcs.values()]
    ring.forEach((npc, i) => {
      const a = (i / Math.max(1, ring.length)) * Math.PI * 2
      npc.setPosition(c.x + Math.cos(a) * 3.4, c.z + Math.sin(a) * 3.4)
      npc.faceTowards(c)
      npc.scripted = true
      npc.timer = 999
    })
    await settle(3)
    return npcs.size
  })

  // Every rigged pose, side by side, so an animation regression is obvious.
  registerDemo('poses', async () => {
    const n = townNode()
    if (n === null) return 0
    setPopulationCap(8)
    const c = townCentre(n)
    const poses = ['wave', 'sit', 'sleep', 'eat', 'cheer', 'dance', 'carry', 'think']
    const rowZ = c.z + 8
    const x0 = c.x - 9
    let i = 0
    for (const pose of poses) {
      let npc = [...npcs.values()][i]
      if (npc === undefined) npc = spawnNeighbour(n, undefined, false) ?? undefined
      if (npc === undefined) break
      npc.scripted = true
      npc.timer = 999
      npc.setPosition(x0 + i * 2.6, rowZ)
      npc.faceTowards({ x: x0 + i * 2.6, y: 0, z: rowZ + 5 })
      npc.act(pose, 0)
      i++
    }
    // The camera follows the kid's monster, so park it in front of the line-up.
    teleportPlayer({ x: x0 + (i * 2.6) / 2, y: 0, z: rowZ })
    await settle(30)
    return i
  })

  registerDemo('creator', async () => {
    app.setPhase('monsterCreator')
    await settle(6)
    return 1
  })

  // End-to-end proof of LAW #1 through this crew: a present appears, the kid
  // taps it, the monster WALKS over, the lid pops, coins fly, it cheers.
  registerDemo('gift-open', async () => {
    const n = townNode()
    if (n === null) return 0
    const id2 = dropGift(n, playerPosition())
    if (id2 === null) return 0
    await settle(3)
    const before = giftOpenCount()
    bus.emit('play:interactRequested', { instanceId: id2, sceneId: n.def.id })
    await settle(150)
    return giftOpenCount() - before
  })

  registerDemo('gift', async () => {
    const n = townNode()
    if (n === null) return 0
    const id2 = dropGift(n, playerPosition())
    await settle(2)
    return id2 === null ? 0 : 1
  })
}
